import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import MarketingShell from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { MailCheck, ArrowLeft, ShieldCheck, Clock, BadgeCheck, ArrowRight, Building2, Eye, EyeOff } from "lucide-react";

export default function ClinicRegister() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/site-content").then(({ data }) => setContent(data)); }, []);

  const [step, setStep] = useState("form"); // form | otp | success
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    clinic_name: "", org_type: "clinic", slug: "", work_email: "", admin_name: "",
    admin_password: "", confirm_password: "", phone: "",
    notes: "",
  });

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitForm = async (e) => {
    e.preventDefault();
    if (form.admin_password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      // brand colors are set later in the clinic's Branding admin page after approval
      const { confirm_password, ...payload } = form;
      const { data } = await api.post("/clinic-registrations/start", payload);
      setVerificationId(data.verification_id);
      setStep("otp");
      setResendIn(30);
      toast.success(`Verification code sent to ${data.email}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not start registration"));
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/clinic-registrations/verify", { verification_id: verificationId, code: otp });
      setStep("success");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Verification failed"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      const { confirm_password, ...payload } = form;
      const { data } = await api.post("/clinic-registrations/start", payload);
      setVerificationId(data.verification_id);
      setOtp("");
      setResendIn(30);
      toast.success("New code sent");
    } catch (err) { toast.error(apiErrorMessage(err, "Could not resend")); }
  };

  return (
    <MarketingShell content={content}>
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-20">
        <div className="flex items-center gap-3 mb-3">
          <Building2 className="h-5 w-5 text-mr-accent" />
          <span className="label-eyebrow text-mr-accent">For healthcare providers</span>
        </div>
        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl text-mr-primary leading-tight">Register your clinic on MedRewards</h1>
        <p className="text-mr-secondary text-lg mt-4 max-w-2xl">Apply in two minutes. Our platform team reviews every application within 1 business day.</p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
          {[
            { icon: ShieldCheck, t: "Work email required", d: "Free email providers (gmail, yahoo…) are not accepted." },
            { icon: MailCheck, t: "Email verification", d: "6-digit code sent to confirm you own the address." },
            { icon: BadgeCheck, t: "Super-admin review", d: "We approve genuine healthcare providers only." },
          ].map((f) => {
            const I = f.icon;
            return (
              <div key={f.t} className="bg-white border border-mr rounded-2xl p-4">
                <I className="h-4 w-4 text-mr-accent" />
                <p className="font-display font-semibold text-mr-primary mt-2 text-sm">{f.t}</p>
                <p className="text-mr-secondary text-xs mt-1">{f.d}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white border border-mr rounded-3xl p-8 lg:p-10 shadow-sm">
          {step === "form" && (
            <form onSubmit={submitForm} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Clinic / Hospital name</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-3">
                  <Input data-testid="cr-name" required value={form.clinic_name} onChange={(e) => setF("clinic_name", e.target.value)} placeholder="St. Mary Medical Center" className="flex-1 rounded-xl bg-mr-bg border-mr h-11" />
                  <div className="inline-flex bg-mr-bg border border-mr rounded-xl p-1 h-11" role="radiogroup" aria-label="Organisation type">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={form.org_type === "clinic"}
                      data-testid="cr-org-type-clinic"
                      onClick={() => setF("org_type", "clinic")}
                      className={`px-4 text-sm font-medium rounded-lg transition-colors ${form.org_type === "clinic" ? "bg-mr-primary text-white shadow-sm" : "text-mr-secondary hover:text-mr-primary"}`}
                    >Clinic</button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={form.org_type === "hospital"}
                      data-testid="cr-org-type-hospital"
                      onClick={() => setF("org_type", "hospital")}
                      className={`px-4 text-sm font-medium rounded-lg transition-colors ${form.org_type === "hospital" ? "bg-mr-primary text-white shadow-sm" : "text-mr-secondary hover:text-mr-primary"}`}
                    >Hospital</button>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2"><Label>Subdomain / slug</Label>
                <div className="flex mt-2">
                  <Input data-testid="cr-slug" required value={form.slug} onChange={(e) => setF("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="stmary" className="rounded-l-xl rounded-r-none bg-mr-bg border-mr h-11" />
                  <span className="px-4 inline-flex items-center bg-[#E2E8F0] border border-l-0 border-mr rounded-r-xl text-sm text-mr-secondary">.medrewards.app</span>
                </div>
              </div>
              <div className="sm:col-span-2"><Label>Work email <span className="text-xs text-mr-secondary">(no gmail/yahoo)</span></Label><Input data-testid="cr-email" type="email" required value={form.work_email} onChange={(e) => setF("work_email", e.target.value)} placeholder="admin@yourclinic.ae" className="mt-2 rounded-xl bg-mr-input border-mr h-11" /></div>
              <div><Label>Your full name (primary admin)</Label><Input data-testid="cr-admin-name" required value={form.admin_name} onChange={(e) => setF("admin_name", e.target.value)} className="mt-2 rounded-xl bg-mr-input border-mr h-11" /></div>
              <div><Label>Phone</Label><Input data-testid="cr-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="+971..." className="mt-2 rounded-xl bg-mr-input border-mr h-11" /></div>

              <div className="sm:col-span-2">
                <Label>Choose a password</Label>
                <div className="mt-2 relative">
                  <Input
                    data-testid="cr-pass"
                    type={showPw ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.admin_password}
                    onChange={(e) => setF("admin_password", e.target.value)}
                    placeholder="At least 6 characters"
                    className="rounded-xl bg-mr-input border-mr h-11 pr-11"
                  />
                  <button
                    type="button"
                    data-testid="cr-pass-toggle"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mr-secondary hover:text-mr-primary"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label>Confirm password</Label>
                <div className="mt-2 relative">
                  <Input
                    data-testid="cr-pass-confirm"
                    type={showConfirm ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.confirm_password}
                    onChange={(e) => setF("confirm_password", e.target.value)}
                    placeholder="Re-enter your password"
                    className={`rounded-xl bg-mr-input border-mr h-11 pr-11 ${form.confirm_password && form.admin_password !== form.confirm_password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                  <button
                    type="button"
                    data-testid="cr-pass-confirm-toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mr-secondary hover:text-mr-primary"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.confirm_password && form.admin_password !== form.confirm_password && (
                  <p className="text-xs text-red-600 mt-1">Passwords don't match</p>
                )}
              </div>

              <div className="sm:col-span-2"><Label>Tell us about your clinic <span className="text-xs text-mr-secondary">(optional)</span></Label><Textarea data-testid="cr-notes" rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} placeholder="Specialties, approximate patient base, why MedRewards…" className="mt-2 rounded-xl bg-mr-input border-mr" /></div>
              <div className="sm:col-span-2 -mt-2 rounded-xl bg-mr-input/60 p-3 text-xs text-mr-secondary">You'll be able to customize your clinic's brand colors, logo and membership-card design from the Admin Branding panel after approval.</div>
              <div className="sm:col-span-2">
                <Button data-testid="cr-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                  {busy ? "Sending verification code…" : "Send verification code"}
                </Button>
                <p className="text-xs text-mr-secondary mt-3 text-center">By submitting, you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
              </div>
            </form>
          )}

          {step === "otp" && (
            <>
              <button data-testid="cr-otp-back" onClick={() => setStep("form")} className="inline-flex items-center gap-1 text-sm text-mr-secondary hover:text-mr-primary mb-6">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><MailCheck className="h-5 w-5 text-mr-accent" /></div>
                <h2 className="font-display text-2xl font-semibold text-mr-primary">Check your work email</h2>
              </div>
              <p className="text-mr-secondary">We sent a 6-digit code to <b className="text-mr-primary">{form.work_email}</b>.</p>

              <div className="mt-8 flex justify-center">
                <InputOTP value={otp} onChange={setOtp} maxLength={6} data-testid="cr-otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-14 w-12 text-2xl font-display font-bold" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button data-testid="cr-otp-verify" onClick={verifyOtp} disabled={busy || otp.length !== 6} className="mt-8 w-full h-11 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                {busy ? "Verifying…" : "Verify & submit application"}
              </Button>

              <div className="mt-6 text-center text-sm text-mr-secondary">
                Didn't get it?{" "}
                <button data-testid="cr-otp-resend" onClick={resend} disabled={resendIn > 0} className="text-mr-primary font-medium underline underline-offset-4 disabled:no-underline disabled:text-mr-secondary">
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </>
          )}

          {step === "success" && (
            <div className="text-center py-6">
              <div className="h-16 w-16 mx-auto rounded-2xl bg-[#E2E8F0] flex items-center justify-center">
                <Clock className="h-8 w-8 text-mr-accent" />
              </div>
              <h2 className="font-display text-3xl font-semibold text-mr-primary mt-6">Application received</h2>
              <p className="text-mr-secondary mt-3 max-w-md mx-auto">
                Thanks, {form.admin_name}! Your application for <b>{form.clinic_name}</b> is under review by our platform team. We'll email you at <b className="text-mr-primary">{form.work_email}</b> once it's approved — usually within 1 business day.
              </p>
              <div className="mt-8 flex justify-center gap-3">
                <Button data-testid="cr-back-home" onClick={() => navigate("/")} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                  Back to home <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
